import {Samples} from "src/app/infrastructure/moovement-api/models/samples";

export enum SynchronizationStatus {
  creationPending = 'creation_pending',
  upToDate = 'up_to_date',
  deleted = 'deleted',
  syncPending = 'sync_pending',
}

export interface Persistable {
  id: string;
  synchronizationStatus?: SynchronizationStatus;
  synchronizationAttempts?: number;
  name?: string;
}

export interface Asset extends Persistable {
  version?: number;
  farmId: string;
}

export interface FixedAsset extends Asset {
  location?: Samples.Position
}

export interface LoRaGateway extends FixedAsset {
  ipAddress?: string;
}

export interface LoRaGatewayStatus {
  id: string;
  deviceId: string;
  instant: string;
  capability: string
  value: string;
  normalisedValue?: string;
}

export interface FixedSamplePoint extends FixedAsset {
  sampleBasedLocation: boolean;
}

export enum WaterPointType {
  UNKNOWN = "UNKNOWN",
  BORE = "BORE",
  DAM = "DAM",
  TANK = "TANK",
  TROUGH = "TROUGH"
}

export interface WaterPoint extends FixedAsset {
  type: WaterPointType
}

export interface Animal extends Asset {
  breed?: Breed
  acquisition?: Acquisition
  disposal?: Disposal
  nlisTag?: string
  visualTag?: string
  comment?: string
  yearOfBirth?: string
  dateOfBirth?: string
  assignedPaddockId?: string
  type?: AnimalType;
  profilePictureUrl?: string
  profilePictureThumbnailUrl?: string,
  animalMetadata?: AnimalMetadata,
}

export interface AnimalMetadata {
   rfid?: string,
  sisbov?: string
}

export interface FarmMetadata {
  eras?: string,
  sif?: string
}

export interface WeightMeasurement {
  id: string
  animalId: string
  weight: number
  unit: WeightMeasurementUnit,
  submittedAt: Date
}

export interface Pregnancy {
  id?: string
  chance?: number
  dueDate?: string
  status?: string
}


export interface Breed {
  version: number
  id: string
  name: string
}

export interface Acquisition {
  event?: string
  date?: string
  cost?: string
}

export interface Disposal {
  event?: string
  date?: string
  proceeds?: string
}

export interface VisualTag {
  colour?: string
  id: string
}

export enum AnimalType {
  UNKNOWN = "UNKNOWN",
  BULL = "BULL",
  BULL_WEANER = "BULL_WEANER",
  BULL_CALF = "BULL_CALF",
  STEER = "STEER",
  STEER_WEANER = "STEER_WEANER",
  STEER_CALF = "STEER_CALF",
  COW = "COW",
  HEIFER = "HEIFER",
  HEIFER_WEANER = "HEIFER_WEANER",
  HEIFER_CALF = "HEIFER_CALF"
}

export enum WeightMeasurementUnit {
  KILOGRAM = "KILOGRAM",
  POUND = "POUND"
}

export function simplify(animalType?: string): string {
  if (!animalType) {
    return "unknown"
  }
  switch (animalType) {
    case AnimalType.STEER:
      return "steer"
    case AnimalType.BULL:
      return "bull"
    case AnimalType.COW:
    case AnimalType.HEIFER:
      return "cow"
    case AnimalType.BULL_WEANER:
    case AnimalType.BULL_CALF:
    case AnimalType.STEER_WEANER:
    case AnimalType.STEER_CALF:
    case AnimalType.HEIFER_WEANER:
    case AnimalType.HEIFER_CALF:
      return "calf"
    default:
      return "unknown"
  }
}

export const allAnimalTypes: AnimalType[] = [
  AnimalType.BULL,
  AnimalType.BULL_WEANER,
  AnimalType.BULL_CALF,
  AnimalType.STEER,
  AnimalType.STEER_WEANER,
  AnimalType.STEER_CALF,
  AnimalType.COW,
  AnimalType.HEIFER,
  AnimalType.HEIFER_WEANER,
  AnimalType.HEIFER_CALF,
]

export interface Paddock extends Persistable {
  version?: number;
  name: string;
  boundaries?: {
    rings?: Array<Array<number>>
    srid?: number
  };
  enclosureId?: string;
}

export interface PaddockGeometry {
  id: string;
  area: number;
}

export interface Device extends Asset {
  typeId: string;
  batchId?: string;
  familyId?: string;
  applicationEui: string;
  provisionedTime?: string;
  applicationKey?: string;
  hardwareId: string;
  ownerId: string;
  capabilities: string[];
}

export interface DeviceAssociation extends Persistable {
  assetId: string,
  deviceId: string,
  version?: number,
  sampleThresholds?: SampleThresholds,
  startDateTime: string,
  endDateTime?: string
}

export interface InterDeviceAssociation {
  id: string;
  associationType: string;
  first: Device;
  second: Device;
}

export interface SampleThresholds {
  WaterLevel?: SampleThreshold
}

export interface SampleThreshold {
  datum?: number
  nominalValue?: number
  thresholds?: Thresholds
}

export interface Thresholds {
  HIGH_WARNING: number,
  HIGH_ALERT: number,
  LOW_WARNING: number,
  LOW_ALERT: number
}
